
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import AdminLayout from './AdminLayout';
import EnhancedEventsTable from '@/components/admin/EnhancedEventsTable';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus, BarChart3, Calendar, Users } from 'lucide-react';
import { Event } from '@/lib/types';
import { fetchMyEvents } from '@/services/events/eventCrudService';
import EventStatisticsPanel from '@/components/admin/EventStatisticsPanel';

const EventsManagement = () => {
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);

  const { data: events = [], isLoading, error } = useQuery({
    queryKey: ['admin-events'],
    queryFn: () => fetchMyEvents()
  });

  const handleViewEvent = (event: Event) => {
    setSelectedEvent(event);
    // TODO: Open event details modal
  };

  const handleEditEvent = (event: Event) => {
    // TODO: Open event editor
    console.log('Edit event:', event);
  };

  const handleDeleteEvent = (event: Event) => {
    // TODO: Open delete confirmation
    console.log('Delete event:', event);
  };

  const totalEvents = (events as Event[]).length;
  const publishedEvents = (events as Event[]).filter((event: Event) => event.status === 'published').length;
  const draftEvents = (events as Event[]).filter((event: Event) => event.status === 'draft').length;
  const totalOrganizers = new Set((events as Event[]).map((event: Event) => event.organizer?.name).filter(Boolean)).size;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Események kezelése</h1>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Új esemény
        </Button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Összes esemény</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalEvents}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Publikált</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{publishedEvents}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Piszkozat</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{draftEvents}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Szervezők</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalOrganizers}</div>
          </CardContent>
        </Card>
      </div>

      {/* Events Table */}
      <Card>
        <CardHeader>
          <CardTitle>Események</CardTitle>
          <CardDescription>
            Összes esemény kezelése és szerkesztése
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              Események betöltése...
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-32 text-destructive">
              Hiba történt az események betöltésekor
            </div>
          ) : (
            <EnhancedEventsTable
              events={events as Event[]}
              onViewEvent={handleViewEvent}
              onEditEvent={handleEditEvent}
              onDeleteEvent={handleDeleteEvent}
            />
          )}
        </CardContent>
      </Card>

      {/* Statistics Panel */}
      <EventStatisticsPanel />
    </div>
  );
};

export default EventsManagement;
